import { LABOR_CATEGORIES, laborCostFromHours, rateProfileForItem } from "./plannerRates";
import { PLANNER_LINE_KINDS } from "./plannerTopics";

function clean(value) {
    return String(value || "").replace(/\s+/g, " ").trim();
}

export function parseEstimateNumber(value, { preferMoney = false, preferHours = false } = {}) {
    const text = clean(value).replace(/,/g, "");
    if (!text) return null;
    const matches = [...text.matchAll(/(\$)?\s*(-?\d+(?:\.\d+)?)\s*([kK])?\s*([a-zA-Z%]*)/g)]
        .map((match) => {
            const number = Number(match[2]);
            if (!Number.isFinite(number)) return null;
            const suffix = match[3] || "";
            const unit = (match[4] || "").toLowerCase();
            return {
                value: suffix ? number * 1000 : number,
                money: Boolean(match[1]) || Boolean(suffix),
                hours: unit.startsWith("h"),
                percent: unit.startsWith("%") || unit === "pct",
            };
        })
        .filter(Boolean);
    if (!matches.length) return null;
    if (preferMoney) return (matches.find((row) => row.money) || matches[0]).value;
    if (preferHours) return (matches.find((row) => row.hours) || matches[0]).value;
    return matches[0].value;
}

export function parsePlainNumber(value) {
    const text = clean(value).replace(/[$,]/g, "");
    if (!text) return null;
    const number = Number.parseFloat(text);
    return Number.isFinite(number) ? number : null;
}

export function lineKindOf(item) {
    const explicit = clean(item?.lineKind).toLowerCase();
    if (!clean(item?.parentId)) return explicit === "component" ? "component" : "job";
    if (PLANNER_LINE_KINDS.some((row) => row.id === explicit) && explicit !== "job") return explicit;
    return "component";
}

export function lineKindLabel(kind) {
    return PLANNER_LINE_KINDS.find((row) => row.id === kind)?.label || "Component";
}

export const PURCHASE_TREATMENTS = [
    { id: "pbr", label: "PBR purchase", description: "Counts in built-up pricing." },
    { id: "customer", label: "Customer purchase", description: "Track the need, but exclude from our price." },
    { id: "note", label: "Note only", description: "Requirement only; no dollar amount." },
];

export function purchaseTreatmentOf(item) {
    const id = clean(item?.purchaseTreatment).toLowerCase();
    return PURCHASE_TREATMENTS.some((row) => row.id === id) ? id : "pbr";
}

export function addQuoteValue(total, key, value) {
    if (value == null) return;
    total[key] = (total[key] || 0) + value;
}

export function emptyQuoteTotal() {
    return { laborHours: null, cost: null, purchases: null, customerPurchases: null, total: null, estimate: null };
}

export function finalizeQuoteTotal(total) {
    const next = { ...emptyQuoteTotal(), ...total };
    next.total = next.cost == null && next.purchases == null ? null : (next.cost || 0) + (next.purchases || 0);
    return next;
}

export function quoteVariance(estimate, builtUp) {
    if (estimate == null || builtUp == null) return null;
    const amount = estimate - builtUp;
    return {
        amount,
        pct: estimate ? amount / estimate : null,
        over: builtUp > estimate + 0.009,
        under: builtUp < estimate - 0.009,
    };
}

export function withContingency(builtUp, percent) {
    if (builtUp == null) return null;
    const pct = Number(percent);
    if (!Number.isFinite(pct) || pct <= 0) return builtUp;
    return builtUp * (1 + pct / 100);
}

export function quoteFromItem(item) {
    const kind = lineKindOf(item);
    const profile = rateProfileForItem(item);
    const isPricedLine = kind === "labor" || kind === "purchase";
    const laborHours = kind === "labor" ? parseEstimateNumber(item?.laborEstimate, { preferHours: true }) : null;
    const manualCost = kind === "labor" ? parseEstimateNumber(item?.costEstimate, { preferMoney: true }) : null;
    const qty = parsePlainNumber(item?.qty);
    const unitCost = parseEstimateNumber(item?.unitCost, { preferMoney: true });
    const purchaseRaw = kind === "purchase" ? parseEstimateNumber(item?.purchaseEstimate, { preferMoney: true }) : null;
    const purchaseTreatment = purchaseTreatmentOf(item);
    const purchaseAmount =
        kind === "purchase" ? (unitCost != null ? unitCost * (qty && qty > 0 ? qty : 1) : purchaseRaw) : null;
    const purchases = purchaseTreatment === "pbr" ? purchaseAmount : null;
    const estimate = parseEstimateNumber(item?.budgetTarget, { preferMoney: true });
    const contingencyPct = parsePlainNumber(item?.contingencyPct);
    const skipOwn = !isPricedLine;
    const rateCost = skipOwn ? null : laborCostFromHours(laborHours, profile);
    const cost = skipOwn ? null : manualCost != null ? manualCost : rateCost;
    const hasCost = cost != null;
    const hasPurchases = purchases != null;
    return {
        kind,
        profile,
        laborCategory: profile?.id,
        laborHours: skipOwn ? null : laborHours,
        cost,
        purchases,
        qty: qty && qty > 0 ? qty : null,
        unitCost,
        purchaseTreatment,
        excludedPurchases: purchaseTreatment === "pbr" ? null : purchaseAmount,
        total: !hasCost && !hasPurchases ? null : (cost || 0) + (purchases || 0),
        estimate,
        contingencyPct: contingencyPct != null && contingencyPct > 0 ? contingencyPct : null,
    };
}

export function mergeQuoteTotals(own, child) {
    const total = emptyQuoteTotal();
    addQuoteValue(total, "laborHours", own?.laborHours);
    addQuoteValue(total, "cost", own?.cost);
    addQuoteValue(total, "purchases", own?.purchases);
    addQuoteValue(total, "customerPurchases", own?.purchaseTreatment === "customer" ? own?.excludedPurchases : null);
    addQuoteValue(total, "laborHours", child?.laborHours);
    addQuoteValue(total, "cost", child?.cost);
    addQuoteValue(total, "purchases", child?.purchases);
    addQuoteValue(total, "customerPurchases", child?.customerPurchases);
    return finalizeQuoteTotal(total);
}

export function emptyMix() {
    return Object.fromEntries(LABOR_CATEGORIES.map((row) => [row.id, { hours: 0, cost: 0 }]));
}

export function addToMix(mix, categoryId, hours, cost) {
    const id = LABOR_CATEGORIES.some((row) => row.id === categoryId) ? categoryId : "design";
    if (!mix[id]) mix[id] = { hours: 0, cost: 0 };
    if (hours) mix[id].hours += hours;
    if (cost) mix[id].cost += cost;
}

export function mixList(mix) {
    return LABOR_CATEGORIES.map((row) => {
        const used = mix?.[row.id] || { hours: 0, cost: 0 };
        return {
            id: row.id,
            label: row.label,
            hourlyRate: row.hourlyRate,
            hours: used.hours || 0,
            cost: used.cost || 0,
        };
    }).filter((row) => row.hours || row.cost);
}

export function itemQuotePreview(item, childQuote) {
    const own = quoteFromItem(item);
    const rolled = childQuote ? mergeQuoteTotals(own, childQuote) : own;
    const loaded = withContingency(rolled.total, own.contingencyPct);
    return {
        ...own,
        childLaborHours: childQuote?.laborHours ?? null,
        childCost: childQuote?.cost ?? null,
        childPurchases: childQuote?.purchases ?? null,
        childCustomerPurchases: childQuote?.customerPurchases ?? null,
        childTotal: childQuote?.total ?? null,
        childEstimates: childQuote?.estimate ?? null,
        rolledLaborHours: rolled.laborHours,
        rolledCost: rolled.cost,
        rolledPurchases: rolled.purchases,
        rolledCustomerPurchases: rolled.customerPurchases,
        rolledTotal: rolled.total,
        loadedTotal: loaded,
        variance: quoteVariance(own.estimate, rolled.total),
        allocation: quoteVariance(own.estimate, childQuote?.estimate ?? null),
    };
}
